import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tDicomAbstractName,
  tDicomAbstractDescription,
  tDicomAbstractSecurityNote,
  tDicomTransferSyntaxName,
} from '../../../helpers/i18n/dicomSyntaxI18n';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import {
  HelpCircle,
  Server,
  ChevronDown,
  ChevronRight,
  Settings,
  AlertTriangle,
  Monitor,
  RefreshCw,
} from 'lucide-react';

import type { Device, Connection } from './types';
import { CreatableSelect } from './custom-components/creatable-select';

import {
  useCreateApplicationEntity,
  useCreateConnection,
} from '../../../helpers/api/pacs/pacs';

import {
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
  TransferSyntaxValue,
  transferSyntaxInfo,
  abstractSyntaxInfo,
} from './types/interfaces';
import {
  defaultSyntaxConfig,
  MANUFACTURER_MODEL_DATA,
} from './types/interfaces';

import {
  generateAETitle,
  generateAEName,
  parseDefaultManufacturers,
  parseDefaultTransferSyntaxes,
  transformSyntaxConfigToAPI,
  Badge,
  Button,
  Input,
  Select,
  Textarea,
  Tooltip,
} from './custom-components/tools';

interface AECreationOptimalFormProps {
  onEntityCreated?: (entity: any) => void;
  onCancel?: () => void;
  isModal?: boolean;
  device?: Device;
  connection?: Connection;
  devicesData?: any;
  isLoadingDevices?: boolean;
  address?: any;
  selectedDeviceId?: number | null;
  isAllDevicesSelected?: boolean;
}

export default function AECreationOptimalForm({
  onEntityCreated,
  onCancel,
  isModal = false,
  device: propDevice,
  connection: propConnection,
  devicesData,
  isLoadingDevices,
  address,
  selectedDeviceId,
  isAllDevicesSelected = false,
}: AECreationOptimalFormProps) {
  const { t } = useTranslation('Organization');
  const createApplicationEntityMutation = useCreateApplicationEntity();
  const createConnectionMutation = useCreateConnection();

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(
    propDevice || null
  );
  const [selectedConnection, setSelectedConnection] =
    useState<Connection | null>(propConnection || null);
  const [syntaxConfig, setSyntaxConfig] =
    useState<DicomSyntaxConfig>(defaultSyntaxConfig);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    secondaryCaptureImageStorage: false,
    ultrasoundImageStorage: false,
    ultrasoundMultiFrameImageStorage: false,
    verificationSopClass: false,
  });
  const [formData, setFormData] = useState({
    aeTitle: '',
    name: '',
    dicomType: '',
    description: '',
    manufacturer: '',
    model: '',
    supportedServices: ['c-echo', 'c-store'],

    aeRole: 'SCP' as 'SCP' | 'SCU',
    remoteHost: '',
    remotePort: '',
    remoteTlsEnabled: false,
    supportsCFindWorklist: false,
    supportsCFindStudy: false,
  });

  const [isCustomAETitle, setIsCustomAETitle] = useState(false);
  const [showAeDefaultConfig, setShowAeDefaultConfig] = useState(false);

  const [dynamicManufacturerModelData, setDynamicManufacturerModelData] =
    useState<Record<string, { label: string; value: string }[]>>(
      MANUFACTURER_MODEL_DATA
    );

  const devices = devicesData || [];

  useEffect(() => {
    if (selectedDevice) {
      const deviceConfig = (selectedDevice as any).defaultConfiguration;

      if (deviceConfig) {
        const deviceManufacturerData = parseDefaultManufacturers(deviceConfig);
        if (Object.keys(deviceManufacturerData).length > 0) {
          setDynamicManufacturerModelData(deviceManufacturerData);
        }

        const deviceSyntaxConfig = parseDefaultTransferSyntaxes(deviceConfig);
        setSyntaxConfig(deviceSyntaxConfig);

        const aeTitlePrefix = deviceConfig.aeTitlePrefix || 'AE_';
        const aeNamePrefix = deviceConfig.aeNamePrefix || 'machine_';

        setFormData((prev) => ({
          ...prev,
          aeTitle: isCustomAETitle
            ? prev.aeTitle
            : generateAETitle(aeTitlePrefix),
          name: prev.name ? prev.name : generateAEName(aeNamePrefix),
        }));
      }
    }
  }, [selectedDevice, isCustomAETitle]);

  useEffect(() => {
    if (!formData.aeTitle.trim()) {
      const aeTitlePrefix =
        (selectedDevice &&
          (selectedDevice as any).defaultConfiguration?.aeTitlePrefix) ||
        'AE_';
      setFormData((prev) => ({
        ...prev,
        aeTitle: generateAETitle(aeTitlePrefix),
      }));
    }
  }, [formData.aeTitle, selectedDevice]);

  useEffect(() => {
    if (!propDevice && !propConnection && devices.length > 0) {
      if (selectedDeviceId && !isAllDevicesSelected) {
        const preSelectedDevice = devices.find(
          (d: any) => d.id === selectedDeviceId
        );
        if (preSelectedDevice) {
          setSelectedDevice(preSelectedDevice);
          if (
            (preSelectedDevice as any).connections &&
            (preSelectedDevice as any).connections.length > 0
          ) {
            setSelectedConnection((preSelectedDevice as any).connections[0]);
          }
          return;
        }
      }

      if ((isAllDevicesSelected || !selectedDeviceId) && !selectedDevice) {
        const firstDevice = devices[0];
        setSelectedDevice(firstDevice);

        if (
          (firstDevice as any).connections &&
          (firstDevice as any).connections.length > 0
        ) {
          setSelectedConnection((firstDevice as any).connections[0]);
        }
      }
    }
  }, [
    devices,
    propDevice,
    propConnection,
    selectedDeviceId,
    isAllDevicesSelected,
  ]);

  useEffect(() => {
    if (
      !propDevice &&
      !propConnection &&
      devices.length > 0 &&
      selectedDeviceId &&
      !isAllDevicesSelected
    ) {
      const newSelectedDevice = devices.find(
        (d: any) => d.id === selectedDeviceId
      );
      if (newSelectedDevice && newSelectedDevice !== selectedDevice) {
        setSelectedDevice(newSelectedDevice);
        setSelectedConnection(null); // Reset connection

        if (
          (newSelectedDevice as any).connections &&
          (newSelectedDevice as any).connections.length > 0
        ) {
          setSelectedConnection((newSelectedDevice as any).connections[0]);
        }
      }
    }
  }, [
    selectedDeviceId,
    devices,
    propDevice,
    propConnection,
    isAllDevicesSelected,
    selectedDevice,
  ]);

  const handleGenerateNewAETitle = () => {
    const aeTitlePrefix =
      (selectedDevice &&
        (selectedDevice as any).defaultConfiguration?.aeTitlePrefix) ||
      'AE_';
    setFormData((prev) => ({
      ...prev,
      aeTitle: generateAETitle(aeTitlePrefix),
    }));
    setIsCustomAETitle(false);
  };

  const handleDeviceChange = (deviceId: string) => {
    const device = devices.find((d: any) => d.id?.toString() === deviceId);
    setSelectedDevice(device || null);
    setSelectedConnection(null);

    if (
      device &&
      (device as any).connections &&
      (device as any).connections.length > 0
    ) {
      setSelectedConnection((device as any).connections[0]);
    }
  };

  const handleConnectionChange = (connectionId: string) => {
    if (!selectedDevice) return;
    const connection = (selectedDevice as any).connections?.find(
      (c: any) => c.id?.toString() === connectionId
    );
    setSelectedConnection(connection || null);
  };

  const isScu = formData.aeRole === 'SCU';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDevice) {
      messageI18n.error('devices.selectDeviceAndConnection');
      return;
    }
    if (!isScu && !selectedConnection) {
      messageI18n.error('devices.selectDeviceAndConnection');
      return;
    }

    if (!formData.name.trim()) {
      messageI18n.error('devices.nameRequired');
      return;
    }

    if (!formData.aeTitle.trim()) {
      messageI18n.error('devices.aeTitleRequired');
      return;
    }

    const remotePortNumber = Number(formData.remotePort);
    if (isScu) {
      if (!formData.remoteHost.trim()) {
        message.error(t('devices.forms.createAe.remoteHostRequired'));
        return;
      }
      if (
        !formData.remotePort ||
        !Number.isInteger(remotePortNumber) ||
        remotePortNumber < 1 ||
        remotePortNumber > 65535
      ) {
        message.error(t('devices.forms.createAe.remotePortInvalid'));
        return;
      }
    }


    const { transferCapabilities, services } = transformSyntaxConfigToAPI(
      syntaxConfig,
      {
        role: isScu ? 'SCU' : 'SCP',
        cFindWorklist: isScu && formData.supportsCFindWorklist,
        cFindStudy: isScu && formData.supportsCFindStudy,
      }
    );

    const getAeType = () => {
      if (!formData.dicomType) {
        return null;
      }
      return formData.dicomType.toUpperCase() === 'STANDARD' ? 'OB' : 'BLIND';
    };

    const apiData = {
      aeTitle: formData.aeTitle,
      name: formData.name,
      description: formData.description,
      ...(formData.manufacturer && { manufacturer: formData.manufacturer }),
      ...(formData.model && { model: formData.model }),
      aeRole: formData.aeRole,
      aeType: getAeType(),
      acceptedCalledAeTitles: formData.aeTitle,
      installed: true,

      associator: isScu,
      associateAcceptor: !isScu,
      status: 'INACTIVE' as const,
      transferCapabilities,
      services,
    };

    const deviceId = parseInt((selectedDevice as any).id, 10);


    let connectionId: number;
    if (isScu) {
      try {
        const created: any = await createConnectionMutation.mutateAsync({
          deviceId,
          connectionData: {
            connectionName: `SCU_${formData.aeTitle}_${remotePortNumber}`,
            hostname: formData.remoteHost.trim(),
            port: remotePortNumber,
            description: `Outbound destination for ${formData.name}`,
            connectionRole: 'SCU_REMOTE',
            tlsEnabled: formData.remoteTlsEnabled,
          },
        });
        const newId =
          created?.data?.id ?? created?.id ?? created?.data?.connection?.id;
        if (!newId) {
          message.error(t('devices.forms.createAe.remoteConnectionFailed'));
          return;
        }
        connectionId = parseInt(String(newId), 10);
      } catch (error) {
        console.error('Error creating SCU destination connection:', error);
        message.error(t('devices.forms.createAe.remoteConnectionFailed'));
        return;
      }
    } else {
      connectionId = parseInt((selectedConnection as any).id, 10);
    }

    createApplicationEntityMutation.mutate(
      {
        deviceId,
        connectionId,
        aeData: apiData,
      },
      {
        onSuccess: (result) => {
          messageI18n.success('devices.aeCreated');
          if (onEntityCreated) {
            onEntityCreated(result);
          }
        },
        onError: (error: unknown) => {
          console.error('Error creating application entity:', error);

          let errorMessage = tToast('common.genericError');

          if (error && typeof error === 'object' && 'message' in error) {
            errorMessage = error.message as string;
          }
          if (error && typeof error === 'object' && 'response' in error) {
            const response = (error as { response?: { data?: unknown } })
              .response;
            if (response?.data) {
              const errorData = response.data as {
                message?: string;
                details?: { error?: string };
                status?: string;
              };

              if (errorData.message) {
                errorMessage = errorData.message;
              } else if (errorData.details?.error) {
                errorMessage = errorData.details.error;
              }
            }
          }

          message.error(errorMessage);
        },
      }
    );
  };

  const updateTransferSyntax = (
    abstractSyntax: keyof DicomSyntaxConfig,
    transferSyntax: string,
    value: TransferSyntaxValue
  ) => {
    setSyntaxConfig((prev) => ({
      ...prev,
      [abstractSyntax]: {
        ...prev[abstractSyntax],
        [transferSyntax]: value,
      },
    }));
  };

  const bulkUpdateAbstractSyntax = (
    abstractSyntax: keyof DicomSyntaxConfig,
    value: TransferSyntaxValue
  ) => {
    setSyntaxConfig((prev) => {
      const updated = { ...prev };
      Object.keys(updated[abstractSyntax]).forEach((transferSyntax) => {
        updated[abstractSyntax][transferSyntax as keyof AbstractSyntaxConfig] =
          value;
      });
      return updated;
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getAcceptedCount = (abstractSyntax: keyof DicomSyntaxConfig) => {
    const config = syntaxConfig[abstractSyntax];
    return Object.values(config).filter((value) => value === 'accepted').length;
  };

  return (
    <div className="mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-600" />
            {t('devices.forms.createAe.title')}
          </h2>
          <p className="text-sm text-slate-600 dark:text-gray-400 mt-1">
            {t('devices.forms.createAe.subtitle')}
          </p>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Machine role: decides whether we listen for this machine or dial it */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                <Server className="h-4 w-4 text-blue-600" />
                {t('devices.forms.createAe.machineRole')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    !isScu
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-200 dark:border-gray-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="aeRole"
                    className="mt-1"
                    checked={!isScu}
                    onChange={() =>
                      setFormData({ ...formData, aeRole: 'SCP' })
                    }
                  />
                  <span>
                    <span className="block font-medium text-slate-900 dark:text-white">
                      {t('devices.forms.createAe.roleScp')}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-gray-400 mt-1">
                      {t('devices.forms.createAe.roleScpHint')}
                    </span>
                  </span>
                </label>

                <label
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isScu
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                      : 'border-slate-200 dark:border-gray-700 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="aeRole"
                    className="mt-1"
                    checked={isScu}
                    onChange={() =>
                      setFormData({ ...formData, aeRole: 'SCU' })
                    }
                  />
                  <span>
                    <span className="block font-medium text-slate-900 dark:text-white">
                      {t('devices.forms.createAe.roleScu')}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-gray-400 mt-1">
                      {t('devices.forms.createAe.roleScuHint')}
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Where to send, for an SCU destination */}
            {isScu && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                  <Monitor className="h-4 w-4 text-blue-600" />
                  {t('devices.forms.createAe.destinationAddress')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('devices.forms.createAe.remoteHost')}
                      <span className="text-red-500">*</span>
                      <Tooltip content={t('devices.tooltips.remoteHost')}>
                        <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                      </Tooltip>
                    </label>
                    <Input
                      value={formData.remoteHost}
                      onChange={(e: any) =>
                        setFormData({ ...formData, remoteHost: e.target.value })
                      }
                      placeholder={t('devices.forms.createAe.remoteHostPlaceholder')}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('devices.forms.createAe.remotePort')}
                      <span className="text-red-500">*</span>
                      <Tooltip content={t('devices.tooltips.remotePort')}>
                        <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                      </Tooltip>
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={65535}
                      value={formData.remotePort}
                      onChange={(e: any) =>
                        setFormData({ ...formData, remotePort: e.target.value })
                      }
                      placeholder="104"
                      className="w-full"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={formData.remoteTlsEnabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        remoteTlsEnabled: e.target.checked,
                      })
                    }
                  />
                  {t('devices.forms.createAe.remoteTls')}
                </label>

                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t('devices.forms.createAe.remoteTlsWarning', {
                    defaultValue:
                      'Must match the peer’s port. TLS on against a plaintext port fails with “terminated the handshake”; TLS off against a TLS port times out instead. Test with Echo after saving.',
                  })}
                </p>

                <p className="text-xs text-slate-500 dark:text-gray-400">
                  {t('devices.forms.createAe.destinationAddressHint')}
                </p>
              </div>
            )}

            {/* Device and Connection Selection */}
            {!propDevice && !propConnection && isAllDevicesSelected && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                  <Monitor className="h-4 w-4 text-blue-600" />
                  {t('devices.forms.createAe.deviceConnectionSelection')}
                </h3>

                <div
                  className={`grid grid-cols-1 gap-4 ${isScu ? '' : 'md:grid-cols-2'}`}
                >
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('devices.forms.createAe.device')}
                      <Tooltip content={t('devices.tooltips.selectDevice')}>
                        <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                      </Tooltip>
                    </label>
                    <Select
                      value={selectedDevice?.id?.toString() || ''}
                      onValueChange={handleDeviceChange}
                      className="w-full"
                    >
                      <option value="">{t('devices.forms.createAe.selectDevice')}</option>
                      {devices.map((device: any) => (
                        <option key={device.id} value={device.id?.toString()}>
                          {device.deviceName || device.name} -{' '}
                          {device.description || t('devices.forms.createDevice.noDescription')}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div className={isScu ? 'hidden' : ''}>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                      {t('devices.forms.createAe.connection')}
                      <Tooltip content={t('devices.tooltips.selectConnection')}>
                        <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                      </Tooltip>
                    </label>
                    <Select
                      value={selectedConnection?.id?.toString() || ''}
                      onValueChange={handleConnectionChange}
                      className="w-full"
                      disabled={!selectedDevice}
                    >
                      <option value="">{t('devices.forms.createAe.selectConnection')}</option>
                      {selectedDevice &&
                        (selectedDevice as any).connections?.map(
                          (connection: any) => (
                            <option
                              key={connection.id}
                              value={connection.id?.toString()}
                            >
                              {connection.connectionName || connection.name} -{' '}
                              {address?.data?.static_ips &&
                              address?.data?.static_ips.length > 0
                                ? address?.data?.static_ips
                                    .map(
                                      (ip: any) =>
                                        `${ip?.address}:${connection?.port}`
                                    )
                                    .join(', ')
                                : `${connection.hostname}:${connection.port}`}
                            </option>
                          )
                        )}
                    </Select>
                  </div>
                </div>

                {isLoadingDevices && (
                  <div className="text-center py-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('devices.forms.createAe.loadingDevices')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Device and Connection Context */}
            {selectedDevice && selectedConnection && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {selectedDevice.name}
                        {!isAllDevicesSelected && selectedDeviceId && (
                          <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                            {t('devices.forms.createAe.preSelected')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-gray-400">
                        {selectedDevice.description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {selectedConnection.name}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-gray-400 font-mono">
                        {address?.data?.static_ips &&
                        address?.data?.static_ips.length > 0
                          ? address?.data?.static_ips
                              .map(
                                (ip: any) =>
                                  `${ip?.address}:${selectedConnection?.port}`
                              )
                              .join(', ')
                          : `${selectedConnection.hostname}:${selectedConnection.port}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Core Configuration Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                <Server className="h-4 w-4 text-blue-600" />
                {t('devices.forms.createAe.coreConfig')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                    {t('devices.forms.createAe.name')}
                    <span className="text-red-500">*</span>
                    <Tooltip content={t('devices.tooltips.machineNameRequired')}>
                      <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                    </Tooltip>
                  </label>
                  <Input
                    placeholder={t('devices.forms.createAe.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('devices.forms.createAe.nameHint')}
                  </p>
                </div>

                {/* Manufacturer Field */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                    {t('devices.forms.createAe.manufacturer')}
                    <Tooltip content={t('devices.tooltips.machineManufacturer')}>
                      <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                    </Tooltip>
                  </label>
                  <CreatableSelect
                    value={formData.manufacturer}
                    options={Object.keys(dynamicManufacturerModelData).map(
                      (manufacturer) => ({
                        label: manufacturer,
                        value: manufacturer,
                      })
                    )}
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        manufacturer: value,
                        model: '', // Reset model when manufacturer changes
                      });
                    }}
                    placeholder={t('devices.forms.createAe.manufacturerPlaceholder')}
                  />
                </div>

                {/* Model Field */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                    {t('devices.forms.createAe.model')}
                    <Tooltip content={t('devices.tooltips.machineModel')}>
                      <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                    </Tooltip>
                  </label>
                  <CreatableSelect
                    value={formData.model}
                    options={
                      formData.manufacturer &&
                      dynamicManufacturerModelData[formData.manufacturer]
                        ? dynamicManufacturerModelData[formData.manufacturer]
                        : []
                    }
                    onChange={(value) => {
                      setFormData({
                        ...formData,
                        model: value,
                      });
                    }}
                    placeholder={
                      formData.manufacturer
                        ? t('devices.forms.createAe.modelPlaceholder', {
                            manufacturer: formData.manufacturer,
                          })
                        : t('devices.forms.createAe.modelPlaceholderNoManufacturer')
                    }
                    isDisabled={!formData.manufacturer}
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                    {t('devices.forms.createAe.aeTitle')}
                    <span className="text-red-500">*</span>
                    <Tooltip content={t('devices.tooltips.machineAeTitle')}>
                      <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                    </Tooltip>
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder={t('devices.forms.createAe.aeTitlePlaceholder')}
                      value={formData.aeTitle}
                      onChange={(e) => {
                        setFormData({ ...formData, aeTitle: e.target.value });
                        setIsCustomAETitle(e.target.value.trim() !== '');
                      }}
                      required
                      className="flex-1"
                      maxLength={15}
                    />
                    <Button
                      type="button"
                      onClick={handleGenerateNewAETitle}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 bg-transparent"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {t('devices.forms.createAe.generate')}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('devices.forms.createAe.aeTitleHint')}
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    {t('devices.forms.createAe.dicomType')}
                    <Tooltip content={t('devices.tooltips.dicomTypeAe')}>
                      <HelpCircle className="h-4 w-4 text-slate-500" />
                    </Tooltip>
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                        formData.dicomType === ''
                          ? 'border-gray-400 bg-gray-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() =>
                        setFormData({ ...formData, dicomType: '' })
                      }
                    >
                      <input
                        type="radio"
                        value=""
                        checked={formData.dicomType === ''}
                        onChange={() =>
                          setFormData({ ...formData, dicomType: '' })
                        }
                        className="text-gray-600"
                      />
                      <span className="text-slate-900 font-medium">{t('devices.forms.createAe.mix')}</span>
                    </div>
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                        formData.dicomType === 'standard'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() =>
                        setFormData({ ...formData, dicomType: 'standard' })
                      }
                    >
                      <input
                        type="radio"
                        value="standard"
                        checked={formData.dicomType === 'standard'}
                        onChange={() =>
                          setFormData({ ...formData, dicomType: 'standard' })
                        }
                        className="text-blue-600"
                      />
                      <span className="text-slate-900 font-medium">
                        {t('devices.forms.createAe.standardOption')}
                      </span>
                    </div>
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                        formData.dicomType === 'blind'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() =>
                        setFormData({ ...formData, dicomType: 'blind' })
                      }
                    >
                      <input
                        type="radio"
                        value="blind"
                        checked={formData.dicomType === 'blind'}
                        onChange={() =>
                          setFormData({ ...formData, dicomType: 'blind' })
                        }
                        className="text-blue-600"
                      />
                      <span className="text-slate-900 font-medium">{t('devices.forms.createAe.blind')}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('devices.forms.createAe.examTypeHintShort')}
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-400 mb-3">
                    {t('devices.forms.createAe.supportedServices')}
                    <Tooltip content={t('devices.tooltips.supportedServicesAe')}>
                      <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                    </Tooltip>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2 rounded-lg border border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-3 opacity-75">
                      <input
                        type="checkbox"
                        value="c-echo"
                        checked={true}
                        disabled={true}
                        className="text-blue-600"
                      />
                      <span className="text-slate-900 dark:text-white font-medium">
                        C-ECHO
                      </span>
                      <span className="text-xs text-slate-500 dark:text-gray-400 ml-auto">
                        {t('devices.common.alwaysEnabled')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 rounded-lg border border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-3 opacity-75">
                      <input
                        type="checkbox"
                        value="c-store"
                        checked={true}
                        disabled={true}
                        className="text-blue-600"
                      />
                      <span className="text-slate-900 dark:text-white font-medium">
                        C-STORE
                      </span>
                      <span className="text-xs text-slate-500 dark:text-gray-400 ml-auto">
                        {t('devices.common.alwaysEnabled')}
                      </span>
                    </div>
                  </div>


                  {isScu && (
                    <div className="mt-3 space-y-2">
                      <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-300 dark:border-gray-600 p-3 hover:border-blue-400">
                        <input
                          type="checkbox"
                          checked={formData.supportsCFindWorklist}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              supportsCFindWorklist: e.target.checked,
                            })
                          }
                          className="mt-0.5 text-blue-600"
                        />
                        <span>
                          <span className="block font-medium text-slate-900 dark:text-white">
                            C-FIND — Modality Worklist
                          </span>
                          <span className="block text-xs text-slate-500 dark:text-gray-400">
                            This machine serves a worklist we can pull scheduled exams from.
                            Enables it in the worklist screen's server picker.
                          </span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-300 dark:border-gray-600 p-3 hover:border-blue-400">
                        <input
                          type="checkbox"
                          checked={formData.supportsCFindStudy}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              supportsCFindStudy: e.target.checked,
                            })
                          }
                          className="mt-0.5 text-blue-600"
                        />
                        <span>
                          <span className="block font-medium text-slate-900 dark:text-white">
                            C-FIND — Study Query/Retrieve
                          </span>
                          <span className="block text-xs text-slate-500 dark:text-gray-400">
                            This machine can be searched for the patients and studies it holds.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('devices.forms.createAe.servicesHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">
                    {t('devices.forms.createAe.descriptionLabel')}
                  </label>
                  <Textarea
                    placeholder={t('devices.forms.createAe.descriptionPlaceholder')}
                    className="min-h-[80px]"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('devices.forms.createAe.descriptionOptionalHint')}
                  </p>
                </div>
              </div>
            </div>
            {/* AE Configuration Button */}
            <div className="border-t border-slate-200 dark:border-gray-700 pt-6">
              <Button
                type="button"
                onClick={() => setShowAeDefaultConfig(!showAeDefaultConfig)}
                variant="outline"
                className="w-full flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  {t('devices.forms.createAe.advancedConfig')}
                </span>
                {showAeDefaultConfig ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
              <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-2">
                {t('devices.forms.createAe.advancedConfigHint')}
              </p>
            </div>

            {/* Abstract Syntax and Transfer Syntax Configuration */}
            {showAeDefaultConfig && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2 text-slate-900 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                    <Settings className="h-4 w-4 text-blue-600" />
                    {t('devices.forms.createAe.abstractSyntaxConfig')}
                  </h3>
                </div>

                {/* Abstract Syntax Configurations */}
                <div className="space-y-4">
                  {Object.entries(abstractSyntaxInfo).map(([key, info]) => {
                    const abstractKey = key as keyof DicomSyntaxConfig;
                    const config = syntaxConfig[abstractKey];
                    const acceptedCount = getAcceptedCount(abstractKey);
                    const totalCount = Object.keys(config).length;

                    return (
                      <div
                        key={key}
                        className="border border-slate-200 dark:border-gray-700 rounded-lg"
                      >
                        <div
                          className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                          onClick={() => toggleSection(key)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {expandedSections[key] ? (
                                <ChevronDown className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                              )}
                              <div>
                                <h4 className="text-base font-medium text-slate-900 dark:text-white">
                                  {tDicomAbstractName(key)}
                                </h4>
                                <p className="text-sm text-slate-600 dark:text-gray-400">
                                  {tDicomAbstractDescription(key)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                className={
                                  acceptedCount > 0
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                }
                              >
                                {t('devices.modals.configureAe.acceptedCount', { accepted: acceptedCount, total: totalCount })}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {expandedSections[key] && (
                          <div className="px-4 pb-4">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-600 dark:text-gray-400">
                                  <strong>{t('devices.common.sopClassUid')}</strong>{' '}
                                  {info.sopClassUid}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      bulkUpdateAbstractSyntax(
                                        abstractKey,
                                        'accepted'
                                      )
                                    }
                                    className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20"
                                  >
                                    {t('devices.common.acceptAll')}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      bulkUpdateAbstractSyntax(
                                        abstractKey,
                                        'rejected'
                                      )
                                    }
                                    className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  >
                                    {t('devices.common.rejectAll')}
                                  </Button>
                                </div>
                              </div>

                              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                                <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                                  <AlertTriangle className="h-4 w-4" />
                                  <span className="text-sm font-medium">
                                    {tDicomAbstractSecurityNote(key)}
                                  </span>
                                </div>
                              </div>

                              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                {/* Transfer Syntax Controls */}
                                <div className="space-y-3">
                                  <h4 className="font-medium text-slate-900 dark:text-white">
                                    {t('devices.common.transferSyntaxSupport')}
                                  </h4>

                                  {/* Uncompressed Transfer Syntaxes */}
                                  <div className="space-y-2">
                                    <h5 className="text-sm font-medium text-slate-700 dark:text-gray-400">
                                      Uncompressed
                                    </h5>
                                    <div className="grid grid-cols-1 gap-2">
                                      {Object.entries(transferSyntaxInfo)
                                        .filter(
                                          ([_, info]) =>
                                            info.type === 'Uncompressed'
                                        )
                                        .map(([transferKey, transferInfo]) => {
                                          if (!(transferKey in config)) {
                                            return null;
                                          }
                                          const value =
                                            config[
                                              transferKey as keyof AbstractSyntaxConfig
                                            ];
                                          return (
                                            <div
                                              key={transferKey}
                                              className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                            >
                                              <div className="flex-1">
                                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                                  {tDicomTransferSyntaxName(transferKey)}
                                                </div>
                                                <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                                  {transferInfo.uid}
                                                </div>
                                              </div>
                                              <Select
                                                value={value}
                                                onValueChange={(
                                                  newValue: TransferSyntaxValue
                                                ) =>
                                                  updateTransferSyntax(
                                                    abstractKey,
                                                    transferKey,
                                                    newValue
                                                  )
                                                }
                                                className="w-24"
                                              >
                                                <option
                                                  value="accepted"
                                                  className="text-green-600"
                                                >
                                                  Accept
                                                </option>
                                                <option
                                                  value="rejected"
                                                  className="text-red-600"
                                                >
                                                  Reject
                                                </option>
                                              </Select>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  </div>

                                  {/* Compressed Transfer Syntaxes */}
                                  <div className="space-y-2">
                                    <h5 className="text-sm font-medium text-slate-700 dark:text-gray-400">
                                      Compressed
                                    </h5>
                                    <div className="grid grid-cols-1 gap-2">
                                      {Object.entries(transferSyntaxInfo)
                                        .filter(
                                          ([_, info]) =>
                                            info.type === 'Compressed'
                                        )
                                        .map(([transferKey, transferInfo]) => {
                                          if (!(transferKey in config)) {
                                            return null;
                                          }
                                          const value =
                                            config[
                                              transferKey as keyof AbstractSyntaxConfig
                                            ];
                                          return (
                                            <div
                                              key={transferKey}
                                              className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                            >
                                              <div className="flex-1">
                                                <div className="text-sm font-medium text-slate-900 dark:text-white">
                                                  {tDicomTransferSyntaxName(transferKey)}
                                                </div>
                                                <div className="text-xs text-slate-500 dark:text-gray-400 font-mono">
                                                  {transferInfo.uid}
                                                </div>
                                              </div>
                                              <Select
                                                value={value}
                                                onValueChange={(
                                                  newValue: TransferSyntaxValue
                                                ) =>
                                                  updateTransferSyntax(
                                                    abstractKey,
                                                    transferKey,
                                                    newValue
                                                  )
                                                }
                                                className="w-24"
                                              >
                                                <option
                                                  value="accepted"
                                                  className="text-green-600"
                                                >
                                                  Accept
                                                </option>
                                                <option
                                                  value="rejected"
                                                  className="text-red-600"
                                                >
                                                  Reject
                                                </option>
                                              </Select>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="min-w-[120px]"
                >
                  {t('Cancel')}
                </Button>
              )}
              <Button
                type="submit"
                disabled={createApplicationEntityMutation.isPending}
                className="min-w-[120px] bg-slate-800 hover:bg-slate-900 text-white"
              >
                {createApplicationEntityMutation.isPending
                  ? t('devices.forms.createAe.creating')
                  : t('devices.forms.createAe.create')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
